<template>
  <div class="generate-prompt-structure">
    <a-page-header title="生成提示词结构" />
    
    <!-- 左右布局 -->
    <div class="main-layout">
      <!-- 左侧：快捷插入面板 -->
      <div class="left-panel">
        <a-card :bordered="false" size="small" class="quick-insert-card">
          <template #title>
            <div style="display: flex; justify-content: space-between; align-items: center">
              <span style="font-weight: 500">快捷插入</span>
              <a-tooltip>
                <template #title>
                  <div>
                    <div><span style="color: #1890ff">●</span> 内容占位符（用于 info_block，会被替换为实际数据）</div>
                    <div style="margin-top: 4px"><span style="color: #52c41a">●</span> 引用占位符（用于 section，会被替换为"参考title为xxx的xml信息块"）</div>
                  </div>
                </template>
                <QuestionCircleOutlined style="color: #999; cursor: help" />
              </a-tooltip>
            </div>
          </template>
          
          <!-- 搜索框 -->
          <a-input-search
            v-model:value="searchKeyword"
            placeholder="搜索变量名或说明..."
            allow-clear
            @search="handleSearch"
            @change="handleSearchChange"
            style="margin-bottom: 12px"
            size="small"
          >
            <template #prefix>
              <span style="color: #999">🔍</span>
            </template>
          </a-input-search>
          
          <a-tabs v-model:activeKey="activeTab" size="small" @change="handleTabChange" class="quick-insert-tabs">
          <!-- 模板预设Tab -->
          <a-tab-pane key="templates">
            <template #tab>
              <span>📋 模板</span>
            </template>
            <div style="display: flex; flex-direction: column; gap: 10px">
              <a-card
                v-for="template in templatePresets"
                :key="template.id"
                size="small"
                :hoverable="true"
                @click="applyTemplate(template)"
                style="cursor: pointer; border-left: 3px solid #1890ff"
                :body-style="{ padding: '12px' }"
              >
                <div style="margin-bottom: 10px">
                  <div style="font-weight: 500; font-size: 14px">{{ template.name }}</div>
                </div>
                <a-button type="primary" size="small" block @click.stop="applyTemplate(template)">
                  使用模板
                </a-button>
              </a-card>
            </div>
          </a-tab-pane>
          
          <!-- 变量列表Tab -->
          <a-tab-pane key="variables">
            <template #tab>
              <span>📐 变量</span>
            </template>
            <div v-if="filteredAllPlaceholders.length === 0" style="padding: 24px; text-align: center; color: #999">
              <a-empty :image="simpleImage" description="暂无匹配的变量" />
            </div>
            <div v-else style="display: flex; flex-direction: column; gap: 8px">
              <template v-for="item in filteredAllPlaceholders" :key="item.placeholder">
                <div class="placeholder-item" :style="{ borderLeftColor: item.highlight_color || '#d9d9d9' }">
                  <!-- 上半部分：变量名和按钮 -->
                  <div class="placeholder-header">
                    <div class="placeholder-info">
                      <div>
                        <span class="placeholder-name">{{ item.placeholder }}</span>
                        <span class="placeholder-display-name">{{ item.display_name }}</span>
                        <!-- 高亮颜色指示器 -->
                        <span v-if="item.highlight_color" class="highlight-indicator" :style="{ backgroundColor: item.highlight_color }" :title="`前端高亮：${item.highlight_color}`"></span>
                      </div>
                    </div>
                    <div class="placeholder-actions">
                      <!-- 内容占位符按钮 -->
                      <a-button
                        size="small"
                        type="primary"
                        @click="insertPlaceholder(item.placeholder)"
                        :title="`插入内容占位符\n用于 info_block，会被替换为实际数据`"
                        style="height: 24px; padding: 0 8px; font-size: 12px"
                      >
                        插入
                      </a-button>
                      <!-- 引用占位符按钮 -->
                      <a-button
                        v-if="item.ref_placeholder"
                        size="small"
                        @click="insertPlaceholder(item.ref_placeholder)"
                        :title="`插入引用占位符\n会被替换为'参考title为${item.title}的xml信息块'`"
                        style="background: #f6ffed; border-color: #b7eb8f; color: #389e0d; height: 24px; padding: 0 6px; font-size: 11px"
                      >
                        ref
                      </a-button>
                    </div>
                  </div>
                  <!-- 下半部分：详细信息 -->
                  <div class="placeholder-meta">
                    <span v-if="item.source_detail" class="meta-item" :title="`数据来源：${item.source_detail}`">
                      📍 {{ item.source_detail }}
                    </span>
                    <span v-if="item.format_type" class="meta-item" :title="`数据格式：${item.format_type}`">
                      📝 {{ formatTypeDisplay(item.format_type) }}
                    </span>
                    <span v-if="item.description" class="meta-item meta-description" :title="item.description">
                      {{ item.description }}
                    </span>
                  </div>
                </div>
              </template>
            </div>
          </a-tab-pane>
        </a-tabs>
        </a-card>
      </div>
      
      <!-- 右侧：输入和结果区域 -->
      <div class="right-panel">
        <!-- 输入描述卡片 -->
        <a-card :bordered="false" size="small" class="input-card">
          <template #title>
            <div style="display: flex; justify-content: space-between; align-items: center">
              <span>输入描述</span>
              <a-button type="link" size="small" @click="showExamples">查看示例</a-button>
            </div>
          </template>
          
          <a-textarea
            ref="textareaRef"
            v-model:value="promptDescription"
            :placeholder="placeholderText"
            :rows="20"
            :maxlength="5000"
            show-count
            @input="handleDescriptionInput"
            class="input-textarea"
          />
          
          <div style="margin-top: 12px; display: flex; justify-content: space-between; align-items: center">
            <a-select
              v-model:value="selectedModelId"
              placeholder="请选择模型"
              style="width: 300px"
              size="small"
              :loading="modelsLoading"
              :dropdown-match-select-width="false"
              :dropdown-style="{ minWidth: '400px' }"
            >
              <a-select-option
                v-for="model in models"
                :key="model.id"
                :value="model.id"
              >
                {{ model.name }} ({{ model.source }})
              </a-select-option>
            </a-select>
            <div>
              <a-button
                type="primary"
                :loading="generating"
                :disabled="!canGenerate"
                @click="handleGenerate"
              >
                生成
              </a-button>
              <a-button style="margin-left: 8px" @click="handleClear">清空</a-button>
            </div>
          </div>
        </a-card>

        <!-- 生成结果区域 -->
        <a-card
          v-if="result || errorMessage"
          :bordered="false"
          size="small"
          style="margin-top: 16px"
          class="result-card"
        >
          <template #title>
            <span>生成结果</span>
          </template>

      <!-- 错误信息 -->
      <a-alert
        v-if="errorMessage"
        :message="errorMessage"
        type="error"
        show-icon
        closable
        @close="errorMessage = ''"
        style="margin-bottom: 16px"
      />

      <!-- 警告信息 -->
      <a-alert
        v-if="warnings && warnings.length > 0"
        :message="'警告信息'"
        type="warning"
        show-icon
        style="margin-bottom: 16px"
      >
        <template #description>
          <ul style="margin: 0; padding-left: 20px">
            <li v-for="(warning, index) in warnings" :key="index">{{ warning }}</li>
          </ul>
        </template>
      </a-alert>

      <!-- 结果展示 -->
      <div v-if="result">
        <CodeEditor
          :model-value="formattedResult"
          language="xml"
          :height="500"
          :read-only="true"
        />
        
        <div style="margin-top: 16px">
          <a-button type="primary" @click="handleCopy">复制XML</a-button>
        </div>
      </div>
        </a-card>

        <!-- 提示信息 -->
        <a-card :bordered="false" size="small" style="margin-top: 16px" class="help-card">
          <a-typography-paragraph style="margin: 0">
            <ul style="margin: 0; padding-left: 20px; font-size: 13px; color: #666">
              <li>从模型配置中选择一个启用的模型</li>
              <li>描述应包含提示词的用途、结构要求、内容要求等信息</li>
              <li>生成的XML结构符合提示词模板规范</li>
            </ul>
          </a-typography-paragraph>
        </a-card>
      </div>
    </div>

    <!-- 示例弹窗 -->
    <a-modal
      v-model:visible="examplesVisible"
      title="示例"
      width="800px"
      :footer="null"
    >
      <a-list :data-source="examples" bordered>
        <template #renderItem="{ item }">
          <a-list-item>
            <a-list-item-meta>
              <template #title>
                <a @click="useExample(item)">{{ item.title }}</a>
              </template>
              <template #description>
                <div style="margin-top: 8px">
                  <a-typography-paragraph strong>输入：</a-typography-paragraph>
                  <pre style="background: #f5f5f5; padding: 8px; border-radius: 4px; white-space: pre-wrap">{{ item.input }}</pre>
                  <a-typography-paragraph strong style="margin-top: 12px">输出：</a-typography-paragraph>
                  <pre style="background: #f5f5f5; padding: 8px; border-radius: 4px; white-space: pre-wrap; max-height: 300px; overflow-y: auto">{{ item.output }}</pre>
                </div>
              </template>
            </a-list-item-meta>
          </a-list-item>
        </template>
      </a-list>
    </a-modal>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { message, Empty } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import CodeEditor from '@/components/CodeEditor.vue';
import { getModels, generatePromptStructure, getPlaceholders } from '@/api/promptSimulator';
import promptTemplates from '@/config/promptTemplates';

export default {
  name: 'GeneratePromptStructure',
  components: {
    CodeEditor,
    QuestionCircleOutlined
  },
  setup() {
    const promptDescription = ref('');
    const selectedModelId = ref(null);
    const models = ref([]);
    const modelsLoading = ref(false);
    const generating = ref(false);
    const result = ref(null);
    const formattedResult = ref('');
    const warnings = ref([]);
    const errorMessage = ref('');
    const examplesVisible = ref(false);
    const textareaRef = ref(null);
    const placeholders = ref({});
    const activeTab = ref('variables'); // 默认显示变量列表
    const searchKeyword = ref('');
    
    // 从配置文件导入的模板（固定3个步骤模板）
    const templatePresets = promptTemplates;

    const placeholderText = `请输入您的原始提示词内容或需求描述...

示例：
你是康明斯发动机诊断AI，名叫"神算子AI诊断仪"，20年实战经验。
根据故障现象 {faultPhenomenon} 和故障码 {faultCodeList}，分析数据流 {streamCsv}。
说话要接地气，不要书面语，称呼用户为"老哥"，用户是小学文化的修理工。
严禁说"不知道"，必须给出排查方向。
输出诊断报告，包含问题定性、排查步骤、维修建议。

系统将自动生成深度结构化的 SystemPrompt XML，包含：
• RoleDefinition (Identity含AgentName + Mission)
• ToneAndStyle (Language, UserAddressing, Tone, ExpressionGuide)
• Constraints (Scope, Safety, Responsiveness, Confidentiality)
• InputData (ECU, FaultInfo, DataStream, KnowledgeBase含knowledge_prompt/dataflow/principle/case/pin_info)
• Workflow (Step1_DataPreprocessing, Step2_Diagnosis, Step3_SolutionGeneration)
• ResponseFormat (Requirement + 多个Section)`;

    const examples = [
      {
        title: '深度结构化 SystemPrompt 示例',
        input: `你是康明斯发动机诊断AI，名叫"神算子AI诊断仪"，20年实战经验。
根据故障现象 {faultPhenomenon} 和故障码 {faultCodeList}，分析数据流 {streamCsv}。
说话要接地气，不要书面语，称呼用户为"老哥"，用户是小学文化的修理工。
严禁说"不知道"，必须给出排查方向。
输出诊断报告，包含问题定性、排查步骤、维修建议。`,
        output: `<SystemPrompt>
  <RoleDefinition>
    <Identity>
      <AgentName>神算子AI诊断仪</AgentName>
      <Profession>维修专家</Profession>
      <Specialty>擅长 {identifiedSystem} 系统故障诊断</Specialty>
      <ExperienceLevel>20年实战经验</ExperienceLevel>
    </Identity>
    <Mission>你的唯一目的是解决 {faultPhenomenon} 这个故障</Mission>
  </RoleDefinition>
  
  <ToneAndStyle>
    <Language>简体中文</Language>
    <UserAddressing>老哥</UserAddressing>
    <AudienceLevel>小学文化的修理工</AudienceLevel>
    <Tone>
      <Positive>自信、直爽、接地气</Positive>
      <Negative>严禁书面语、严禁掉书袋</Negative>
    </Tone>
    <ExpressionGuide>
      <Do>你先查这个、咱们从简单的开始</Do>
      <Dont>建议去4S店、理论上来说</Dont>
    </ExpressionGuide>
  </ToneAndStyle>
  
  <Constraints>
    <Scope>只讨论故障诊断相关话题</Scope>
    <Safety>不涉及危险操作指导</Safety>
    <Responsiveness>严禁回答"不知道"，必须给出排查方向</Responsiveness>
    <Confidentiality>不透露系统内部实现细节</Confidentiality>
  </Constraints>
  
  <InputData>
    <ECU>
      <identifiedSystem>{identifiedSystem}</identifiedSystem>
      <ecuVersionInfoList>{ecuVersionInfoList}</ecuVersionInfoList>
    </ECU>
    <FaultInfo>
      <faultPhenomenon>{faultPhenomenon}</faultPhenomenon>
      <faultCodeList>{faultCodeList}</faultCodeList>
      <StatusRule>历史故障优先级低，未定义故障需要特别说明</StatusRule>
    </FaultInfo>
    <DataStream>
      <working_conditions>{working_conditions}</working_conditions>
      <selected_streams>{selected_streams}</selected_streams>
      <working_hours>{working_hours}</working_hours>
      <streamCsv>{streamCsv}</streamCsv>
    </DataStream>
    <KnowledgeBase>
      <knowledge_prompt>{knowledge_prompt}</knowledge_prompt>
      <dataflow>{dataflow}</dataflow>
      <principle>{principle}</principle>
      <case>{case}</case>
      <pin_info>{pin_info}</pin_info>
    </KnowledgeBase>
  </InputData>
  
  <Workflow>
    <Step1_DataPreprocessing>
      <Instruction>清洗和归类数据流</Instruction>
      <Rules>
        <Grouping>按系统归类数据流</Grouping>
        <FilterDefaults>过滤 FF、8000 等默认值</FilterDefaults>
        <UnitCorrection>修正25000V为2.5V（乘以0.0001）</UnitCorrection>
        <SystemIsolation>后处理系统独立时，只看后处理数据</SystemIsolation>
      </Rules>
    </Step1_DataPreprocessing>
    <Step2_Diagnosis>
      <Strategy>先易后难，从明显异常开始排查</Strategy>
    </Step2_Diagnosis>
    <Step3_SolutionGeneration>
      <Requirements>引用针脚电压、线束连接等具体信息</Requirements>
    </Step3_SolutionGeneration>
  </Workflow>
  
  <ResponseFormat>
    <Requirement>使用Markdown格式，一次性生成完整报告</Requirement>
    <Section title="【问题定性】">
      <Content>用一句话说清楚是什么故障</Content>
    </Section>
    <Section title="【排查步骤】">
      <Content>使用HTML标签构建列表：&lt;ol&gt;&lt;li&gt;&lt;p&gt;&lt;strong&gt;步骤标题&lt;/strong&gt;&lt;/p&gt;具体操作&lt;/li&gt;&lt;/ol&gt;</Content>
    </Section>
    <Section title="【维修建议】">
      <Content>给出明确的维修方向和注意事项</Content>
    </Section>
  </ResponseFormat>
</SystemPrompt>`
      },
      {
        title: '故障诊断助手示例',
        input: `创建一个故障诊断AI助手：

角色：康明斯发动机诊断AI专家

语气和风格：专业、严谨

约束条件：基于实际数据分析

输入数据：
- 故障现象: {faultPhenomenon}
- 故障码: {faultCodeList}
- 数据流: {csv_content}

处理步骤：分析故障、识别原因、提供建议

输出格式：诊断报告`,
        output: `<SystemPrompt>
  <RoleDefinition>
    你是康明斯发动机诊断AI专家，具备深厚的发动机故障诊断经验和数据分析能力。
  </RoleDefinition>
  
  <ToneAndStyle>
    专业、严谨、结构化表达
  </ToneAndStyle>
  
  <Constraints>
    必须基于实际数据进行分析，不得臆测
  </Constraints>
  
  <InputData>
    <faultPhenomenon>{faultPhenomenon}</faultPhenomenon>
    <faultCodeList>{faultCodeList}</faultCodeList>
    <csv_content>{csv_content}</csv_content>
  </InputData>
  
  <Workflow>
    <Step id="1" name="分析故障">
      分析故障现象和故障码
    </Step>
    <Step id="2" name="识别原因">
      基于数据流识别故障原因
    </Step>
    <Step id="3" name="提供建议">
      生成诊断建议
    </Step>
  </Workflow>
  
  <ResponseFormat>
    <Section name="诊断报告">
      包含诊断摘要、原因分析、维修建议
    </Section>
  </ResponseFormat>
</SystemPrompt>`
      }
    ];

    const canGenerate = computed(() => {
      return promptDescription.value.trim().length >= 10 && selectedModelId.value !== null && !generating.value;
    });
    
    // 过滤后的全部变量列表
    const filteredAllPlaceholders = computed(() => {
      const allList = placeholders.value.all || [];
      if (!searchKeyword.value || !searchKeyword.value.trim()) {
        return allList;
      }
      
      const keyword = searchKeyword.value.trim().toLowerCase();
      return allList.filter(item => {
        return (
          item.placeholder.toLowerCase().includes(keyword) ||
          item.display_name.toLowerCase().includes(keyword) ||
          (item.description && item.description.toLowerCase().includes(keyword)) ||
          (item.source_detail && item.source_detail.toLowerCase().includes(keyword))
        );
      });
    });

    // 加载模型列表
    const loadModels = async () => {
      modelsLoading.value = true;
      try {
        const response = await getModels();
        if (response.code === 200) {
          models.value = response.data || [];
          if (models.value.length > 0 && !selectedModelId.value) {
            selectedModelId.value = models.value[0].id;
          }
        } else {
          message.error(response.message || '获取模型列表失败');
        }
      } catch (error) {
        console.error('加载模型列表失败:', error);
        message.error('加载模型列表失败');
      } finally {
        modelsLoading.value = false;
      }
    };

    // 处理描述输入
    const handleDescriptionInput = () => {
      errorMessage.value = '';
      result.value = null;
    };

    // 生成XML结构
    const handleGenerate = async () => {
      if (!canGenerate.value) {
        return;
      }

      generating.value = true;
      errorMessage.value = '';
      result.value = null;
      warnings.value = [];
      formattedResult.value = '';

      try {
        const response = await generatePromptStructure({
          prompt_description: promptDescription.value.trim(),
          model_id: selectedModelId.value
        });

        if (response.code === 200) {
          result.value = response.data.xml_structure;
          formattedResult.value = response.data.formatted || response.data.xml_structure;
          warnings.value = response.data.warnings || [];
          message.success('生成成功');
        } else {
          errorMessage.value = response.message || '生成失败';
          if (response.data && response.data.errors) {
            errorMessage.value += ': ' + response.data.errors.join('; ');
          }
          // 即使有错误，也显示部分结果
          if (response.data && response.data.xml_structure) {
            result.value = response.data.xml_structure;
            formattedResult.value = response.data.formatted || response.data.xml_structure;
          }
        }
      } catch (error) {
        console.error('生成失败:', error);
        errorMessage.value = error.message || '生成失败，请稍后重试';
      } finally {
        generating.value = false;
      }
    };

    // 清空
    const handleClear = () => {
      promptDescription.value = '';
      result.value = null;
      formattedResult.value = '';
      errorMessage.value = '';
      warnings.value = [];
    };

    // 复制XML
    const handleCopy = async () => {
      if (!formattedResult.value) {
        message.warning('没有可复制的内容');
        return;
      }

      try {
        await navigator.clipboard.writeText(formattedResult.value);
        message.success('已复制到剪贴板');
      } catch (error) {
        console.error('复制失败:', error);
        message.error('复制失败');
      }
    };

    // 显示示例
    const showExamples = () => {
      examplesVisible.value = true;
    };

    // 使用示例
    const useExample = (example) => {
      promptDescription.value = example.input;
      examplesVisible.value = false;
      message.info('示例已填充到输入框');
    };

    // 加载占位符列表
    const loadPlaceholders = async () => {
      try {
        const response = await getPlaceholders();
        if (response.code === 200) {
          placeholders.value = response.data || {};
        } else {
          console.error('加载占位符列表失败:', response.message);
        }
      } catch (error) {
        console.error('加载占位符列表失败:', error);
      }
    };


    // 格式化数据类型显示
    const formatTypeDisplay = (formatType) => {
      const typeMap = {
        'string': '字符串',
        'json_array': 'JSON数组',
        'json_object': 'JSON对象'
      };
      return typeMap[formatType] || formatType;
    };

    // 处理搜索
    const handleSearch = () => {
      // 搜索时不切换Tab，在当前Tab中筛选
    };

    // 处理搜索输入变化
    const handleSearchChange = () => {
      // 实时过滤
    };

    // 生成引用占位符
    const getRefPlaceholder = (placeholder) => {
      // 从 {variable_name} 格式中提取 variable_name
      const match = placeholder.match(/\{([^}]+)\}/);
      if (match && match[1]) {
        return `{ref:${match[1]}}`;
      }
      // 如果格式不对，尝试直接添加 ref:
      return placeholder.replace('{', '{ref:');
    };

    // 生成引用占位符按钮的标题
    const getRefPlaceholderTitle = (item) => {
      const refPlaceholder = getRefPlaceholder(item.placeholder);
      const title = item.title || item.display_name;
      return `插入引用占位符: ${refPlaceholder}\n用于 section，会被替换为"参考title为${title}的xml信息块"\n${item.description || item.display_name}`;
    };

    // 插入占位符到光标位置
    const insertPlaceholder = (placeholder) => {
      if (!textareaRef.value) {
        message.warning('无法获取文本域引用');
        return;
      }

      const textarea = textareaRef.value.$el?.querySelector('textarea') || textareaRef.value.$el;
      if (!textarea) {
        message.warning('无法找到文本域元素');
        return;
      }

      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const text = promptDescription.value;
      
      const newText = text.substring(0, start) + placeholder + text.substring(end);
      promptDescription.value = newText;
      
      // 设置光标位置
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + placeholder.length;
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
      
      message.success(`已插入: ${placeholder}`);
    };

    // 切换标签页
    const handleTabChange = () => {
      // 标签页切换时不需要特殊处理
    };
    
    // 应用模板
    const applyTemplate = (template) => {
      promptDescription.value = template.content;
      message.success(`已应用模板: ${template.name}`);
      // 滚动到输入框
      setTimeout(() => {
        if (textareaRef.value) {
          const textarea = textareaRef.value.$el?.querySelector('textarea') || textareaRef.value.$el;
          if (textarea) {
            textarea.focus();
            textarea.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 100);
    };

    onMounted(() => {
      loadModels();
      loadPlaceholders();
    });

    return {
      promptDescription,
      selectedModelId,
      models,
      modelsLoading,
      generating,
      result,
      formattedResult,
      warnings,
      errorMessage,
      examplesVisible,
      placeholderText,
      examples,
      canGenerate,
      textareaRef,
      placeholders,
      activeTab,
      searchKeyword,
      templatePresets,
      filteredAllPlaceholders,
      simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
      handleDescriptionInput,
      handleGenerate,
      handleClear,
      handleCopy,
      showExamples,
      useExample,
      formatTypeDisplay,
      handleSearch,
      handleSearchChange,
      getRefPlaceholder,
      getRefPlaceholderTitle,
      insertPlaceholder,
      handleTabChange,
      applyTemplate
    };
  }
};
</script>

<style scoped>
.generate-prompt-structure {
  padding: 24px;
}

/* 左右布局 */
.main-layout {
  display: flex;
  gap: 16px;
  margin-top: 16px;
  align-items: flex-start;
}

.left-panel {
  width: 380px;
  flex-shrink: 0;
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.right-panel {
  flex: 1;
  min-width: 0;
}

/* 快捷插入卡片样式 */
.quick-insert-card :deep(.ant-card-body) {
  padding: 12px;
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}

.quick-insert-tabs :deep(.ant-tabs-nav) {
  margin-bottom: 8px;
}

.quick-insert-tabs :deep(.ant-tabs-tab) {
  padding: 6px 12px;
  font-size: 12px;
}

.quick-insert-tabs :deep(.ant-tabs-content) {
  max-height: calc(100vh - 320px);
  overflow-y: auto;
}

/* 输入卡片样式 */
.input-card :deep(.ant-card-body) {
  padding: 16px;
}

.input-textarea {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
}

/* 结果卡片样式 */
.result-card :deep(.ant-card-body) {
  padding: 16px;
}

/* 帮助卡片样式 */
.help-card :deep(.ant-card-body) {
  padding: 12px;
}

pre {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
}

/* 占位符项样式 */
.placeholder-item {
  padding: 10px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-left: 3px solid #d9d9d9;
  border-radius: 4px;
  transition: all 0.3s;
}

.placeholder-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left-width: 4px;
}

.placeholder-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
  gap: 8px;
}

.placeholder-info {
  flex: 1;
  min-width: 0;
}

.placeholder-name {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #1890ff;
  font-weight: 500;
  padding: 2px 4px;
  background: #e6f7ff;
  border-radius: 3px;
  word-break: break-all;
  display: inline-block;
  margin-bottom: 4px;
}

.placeholder-display-name {
  font-size: 13px;
  color: #262626;
  font-weight: 500;
  display: inline-block;
  margin-left: 6px;
}

.highlight-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #d9d9d9;
  display: inline-block;
  margin-left: 4px;
  vertical-align: middle;
}

.placeholder-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.placeholder-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  color: #8c8c8c;
  padding-top: 6px;
  border-top: 1px dashed #e8e8e8;
}

.meta-item {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  line-height: 1.4;
}

.meta-description {
  flex: 1;
  font-style: italic;
}
</style>

